/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Pure quote-lifecycle derivations (quote/order separation, 2026-06-19).
 *
 * Import-free + structural so both halves can import it (Slice 1b
 * gates confirmQuote / createPaymentIntent on `isExpired`). `now` is always
 * injected — never `Date.now()` inside — so callers own the clock and tests stay
 * deterministic.
 *
 * EXPIRED is NOT a stored status: it is derived over SENT via `metadata.sentAt`.
 * `sentAt` MUST be an ISO-8601 string; a Firestore Timestamp would make the date
 * math NaN. The helpers defend against a non-string `sentAt` (treat as absent)
 * rather than silently mis-classify.
 */

export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export interface QuoteLifecycleLike {
  status: string;
  metadata?: { sentAt?: string | null } | null;
  onHoldFollowUpAt?: string | null;
}

export type QuoteLifecycleBucket =
  | 'DRAFT'
  | 'SENT'
  | 'EXPIRED'
  | 'ON_HOLD'
  | 'LOST'
  | 'SUPERSEDED'
  | 'GRADUATED';

/** The ISO `sentAt` string, or null when never sent / not a valid string. */
export const quotationDate = (q: QuoteLifecycleLike): string | null => {
  const sentAt = q.metadata?.sentAt;
  return typeof sentAt === 'string' ? sentAt : null;
};

/** True when a SENT quote has aged past its 30-day validity. Strict `<` so
 *  exactly-30-days is still valid. Non-SENT / never-sent / non-string → false. */
export const isExpired = (q: QuoteLifecycleLike, now: number): boolean => {
  if (q.status !== 'SENT') return false;
  const sent = quotationDate(q);
  if (sent === null) return false;
  const sentMs = new Date(sent).getTime();
  if (!Number.isFinite(sentMs)) return false;
  return sentMs + THIRTY_DAYS_MS < now;
};

/** True when an ON_HOLD quote's follow-up date has arrived (≤ now). */
export const isFollowUpDue = (q: QuoteLifecycleLike, now: number): boolean => {
  if (q.status !== 'ON_HOLD') return false;
  const at = q.onHoldFollowUpAt;
  if (typeof at !== 'string') return false;
  const atMs = new Date(at).getTime();
  if (!Number.isFinite(atMs)) return false;
  return atMs <= now;
};

/** Single funnel classifier the quotes dashboard filters on. Legacy `REJECTED`
 *  maps to LOST (read-boundary fallback — never GRADUATED, which would graduate
 *  a dead quote onto the order surface). Post-sale / unknown → GRADUATED. */
export const quoteLifecycleBucket = (q: QuoteLifecycleLike, now: number): QuoteLifecycleBucket => {
  switch (q.status) {
    case 'DRAFT':
      return 'DRAFT';
    case 'SENT':
      return isExpired(q, now) ? 'EXPIRED' : 'SENT';
    case 'ON_HOLD':
      return 'ON_HOLD';
    case 'LOST':
    case 'REJECTED': // legacy: removed from the type, mapped to LOST on read
      return 'LOST';
    case 'SUPERSEDED':
      return 'SUPERSEDED';
    default:
      // CLIENT_CONFIRMED / PARTIALLY_PAID / FULLY_PAID / PAYMENT_RECEIVED and any
      // unknown stored value: an order, or otherwise excluded from the pre-sale funnel.
      return 'GRADUATED';
  }
};
